import { useState } from 'react';
import type { JSX } from 'react';
import { AP_IMPORT_DATEI_MAX_BYTES } from '@asas/domain/ap-import-gruende';
import type { ApVorschau, TenantSettings } from '@asas/domain';

import { anmeldeUrlMitRueckkehr, erzeugeEntwurfsspeicher } from '../daten/entwurf';
import { klassifiziereFehler } from '../daten/fehler';
import type { FehlerBefund } from '../daten/fehler';
import { entitaetsSchluessel, geteilterSerialisierer } from '../daten/serialisierung';
import { trpc, trpcApImportGrund } from '../trpc';

import {
  ABWEISUNG_TEXTE,
  alsBase64,
  formatAufNeuenStand,
  formatEingabe,
  formatEntwurfAus,
  formatMeldungen,
  istFormatGeaendert,
  LEERES_FORMAT,
  pruefeGemerktesFormat,
} from './ap-import';
import type { FormatEntwurf, FormatFeld } from './ap-import';
import { ApImportAnsicht } from './ApImportAnsicht';

export interface ApImportBereichProps {
  /** Die Kontokennung der angemeldeten Person — Inhaberin eines Entwurfs (AC-016). */
  readonly inhaber: string;
  /** Mit `tenant:configure` (`darfApFormatPflegen`)? Ohne das Recht kein Formular, kein 403. */
  readonly darfFormatPflegen: boolean;
}

/** Prüfen und Übernehmen laufen nacheinander, nie gleichzeitig (apps/web/CLAUDE.md). */
const IMPORT_SCHLUESSEL = entitaetsSchluessel('work_package_library', 'import');
const ENTWURFS_SCHLUESSEL = entitaetsSchluessel('ap-import-format', 'mandant');

type Meldungen = Partial<Record<FormatFeld, string>>;

/**
 * Stammarbeitspakete importieren (AC-302). Gezeigt nur mit
 * `work_package_library:manage` (App.tsx); geladen wird erst auf Klick.
 *
 * Zwei Schritte (E1): „Prüfen“ lädt die Datei hoch und zeigt die Vorschau,
 * „Übernehmen“ schreibt. Die Datei bleibt bis zur nächsten Auswahl im Browser,
 * damit „Erneut prüfen“ nach einem Konflikt ohne neue Auswahl geht. Das Format
 * (E10) pflegt nur, wer `tenant:configure` hat.
 */
export function ApImportBereich({ inhaber, darfFormatPflegen }: ApImportBereichProps): JSX.Element {
  const [geoeffnet, setGeoeffnet] = useState(false);
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [ladefehler, setLadefehler] = useState<string | null>(null);
  const [gesperrt, setGesperrt] = useState(false);
  const [datei, setDatei] = useState<File | null>(null);
  const [vorschau, setVorschau] = useState<ApVorschau | null>(null);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [anmelden, setAnmelden] = useState(false);

  const [entwurf, setEntwurf] = useState<FormatEntwurf>(LEERES_FORMAT);
  const [basis, setBasis] = useState<FormatEntwurf>(LEERES_FORMAT);
  const [formSendet, setFormSendet] = useState(false);
  const [formFehler, setFormFehler] = useState<string | null>(null);
  const [formMeldung, setFormMeldung] = useState<string | null>(null);
  const [feldMeldungen, setFeldMeldungen] = useState<Meldungen>({});
  const [hinweise, setHinweise] = useState<Meldungen>({});

  const entwuerfe = erzeugeEntwurfsspeicher({ inhaber });
  const anmeldeUrl = anmeldeUrlMitRueckkehr(
    typeof window === 'undefined' ? '/' : window.location.pathname,
  );

  /** Eigene Eingaben im Formular vor der Neuanmeldung sichern; ohne Eingaben kein Entwurf. */
  function formularSichern(): void {
    if (darfFormatPflegen && istFormatGeaendert(entwurf, basis)) {
      entwuerfe.merken(ENTWURFS_SCHLUESSEL, { entwurf, basis });
    }
  }

  function befundZeigen(befund: FehlerBefund, art: 'laden' | 'schreiben'): void {
    if (befund.art === 'zugriff') setGesperrt(true);
    if (befund.neuAnmelden) {
      formularSichern();
      setAnmelden(true);
    }
    if (art === 'laden') setLadefehler(befund.meldung);
  }

  async function laden(): Promise<void> {
    setLaedt(true);
    setLadefehler(null);

    try {
      const neu = await trpc.tenant.settings.query();
      const neuerStand = formatEntwurfAus(neu.apImport);

      if (settings === null) {
        // Erstes Laden: ein Entwurf von vor der Neuanmeldung wird angeboten, nie abgeschickt.
        const gemerkt = darfFormatPflegen
          ? entwuerfe.holen(ENTWURFS_SCHLUESSEL, pruefeGemerktesFormat)
          : null;
        if (gemerkt === null) {
          setEntwurf(neuerStand);
          setHinweise({});
        } else {
          const stand = formatAufNeuenStand(
            gemerkt.eingabe.entwurf,
            gemerkt.eingabe.basis,
            neuerStand,
          );
          setEntwurf(stand.entwurf);
          setHinweise(stand.hinweise);
          setFormMeldung(
            'Ihre Eingaben von vor der Anmeldung sind wiederhergestellt, aber nicht gespeichert.',
          );
        }
      } else {
        const stand = formatAufNeuenStand(entwurf, basis, neuerStand);
        setEntwurf(stand.entwurf);
        setHinweise(stand.hinweise);
      }

      setBasis(neuerStand);
      setSettings(neu);
    } catch (error) {
      const befund = klassifiziereFehler(error, { schreibzugriff: false });
      if (befund.art !== 'abgebrochen') befundZeigen(befund, 'laden');
    } finally {
      setLaedt(false);
    }
  }

  function importFehler(error: unknown, vorgang: 'pruefen' | 'uebernehmen'): void {
    const befund = klassifiziereFehler(error, { schreibzugriff: true });
    const grund = trpcApImportGrund(error);

    if (befund.art === 'abgebrochen' && !befund.moeglicherweiseAusgefuehrt) return;
    befundZeigen(befund, 'schreiben');

    if (grund !== null) {
      setFehler(ABWEISUNG_TEXTE[grund]);
    } else if (befund.moeglicherweiseAusgefuehrt && vorgang === 'uebernehmen') {
      setFehler(
        `${befund.meldung} Der Import könnte bereits übernommen sein; „Erneut prüfen“ zeigt den Stand.`,
      );
    } else {
      setFehler(befund.meldung);
    }
  }

  async function pruefen(): Promise<void> {
    if (datei === null || sendet || laedt || gesperrt) return;

    // Vor dem Senden: eine zu große Datei käme am Server als 413 an, und die
    // sähe im Browser wie ein Netzfehler aus (trpc.ts, MAX_URL_LENGTH).
    if (datei.size > AP_IMPORT_DATEI_MAX_BYTES) {
      setVorschau(null);
      setMeldung(null);
      setFehler(ABWEISUNG_TEXTE.zu_gross);
      return;
    }

    setSendet(true);
    setFehler(null);
    setMeldung(null);

    try {
      const dateiBase64 = alsBase64(new Uint8Array(await datei.arrayBuffer()));
      const ergebnis = await geteilterSerialisierer.reihe(IMPORT_SCHLUESSEL, () =>
        trpc.workPackageImport.vorschau.mutate({ dateiName: datei.name, dateiBase64 }),
      );

      setVorschau(ergebnis);
    } catch (error) {
      setVorschau(null);
      importFehler(error, 'pruefen');
    } finally {
      setSendet(false);
    }
  }

  async function uebernehmen(): Promise<void> {
    if (vorschau === null || sendet || laedt || gesperrt) return;

    setSendet(true);
    setFehler(null);
    setMeldung(null);

    try {
      const ergebnis = await geteilterSerialisierer.reihe(IMPORT_SCHLUESSEL, () =>
        trpc.workPackageImport.uebernehmen.mutate({ laufId: vorschau.laufId }),
      );

      setVorschau(null);
      setMeldung(
        `Übernommen: ${String(ergebnis.neu)} neu, ${String(ergebnis.geaendert)} geändert.`,
      );
    } catch (error) {
      importFehler(error, 'uebernehmen');
    } finally {
      setSendet(false);
    }
  }

  async function formatSpeichern(): Promise<void> {
    if (settings === null || formSendet || laedt || gesperrt) return;

    const eingabe = formatEingabe(entwurf);
    setFormFehler(null);
    setFormMeldung(null);

    if ('befund' in eingabe) {
      setFeldMeldungen(formatMeldungen([eingabe.befund]));
      return;
    }

    setFeldMeldungen({});
    setFormSendet(true);

    try {
      const neu = await geteilterSerialisierer.reihe(
        entitaetsSchluessel('tenant_settings', settings.id),
        () =>
          trpc.tenant.updateSettings.mutate({
            expectedVersion: settings.version,
            apImport: eingabe.format,
          }),
      );
      const stand = formatEntwurfAus(neu.apImport);

      setSettings(neu);
      setEntwurf(stand);
      setBasis(stand);
      setHinweise({});
      entwuerfe.vergessen(ENTWURFS_SCHLUESSEL);
      // Eine Vorschau mit dem alten Format gilt nicht mehr (E1).
      setVorschau(null);
      setFormMeldung('Das Format ist gespeichert.');
    } catch (error) {
      const befund = klassifiziereFehler(error, { schreibzugriff: true });

      if (befund.art === 'abgebrochen' && !befund.moeglicherweiseAusgefuehrt) return;
      befundZeigen(befund, 'schreiben');
      setFeldMeldungen(formatMeldungen(befund.felder));
      setFormFehler(
        befund.art === 'konflikt'
          ? 'Die Konfiguration wurde inzwischen gespeichert; nichts wurde geschrieben. Bitte „Neu laden“ — Ihre Eingaben bleiben erhalten.'
          : befund.art === 'eingabe' && befund.felder.length > 0
            ? 'Das Format wurde nicht gespeichert. Bitte die markierten Felder prüfen.'
            : befund.moeglicherweiseAusgefuehrt
              ? `${befund.meldung} Das Format könnte bereits gespeichert sein; „Neu laden“ zeigt den Stand.`
              : befund.meldung,
      );
    } finally {
      setFormSendet(false);
    }
  }

  return (
    <ApImportAnsicht
      geoeffnet={geoeffnet}
      onOeffnen={() => {
        setGeoeffnet(true);
        void laden();
      }}
      format={settings === null ? undefined : settings.apImport}
      laedt={laedt}
      sendet={sendet}
      gesperrt={gesperrt}
      ladefehler={ladefehler}
      dateiName={datei?.name ?? null}
      vorschau={vorschau}
      fehler={fehler}
      meldung={meldung}
      anmeldeUrl={anmelden ? anmeldeUrl : null}
      onNeuLaden={() => {
        void laden();
      }}
      onDatei={(neu) => {
        setDatei(neu);
        setVorschau(null);
        setFehler(null);
        setMeldung(null);
      }}
      onPruefen={() => {
        void pruefen();
      }}
      onUebernehmen={() => {
        void uebernehmen();
      }}
      onNeuAnmelden={formularSichern}
      formular={
        darfFormatPflegen
          ? {
              entwurf,
              geaendert: istFormatGeaendert(entwurf, basis),
              sendet: formSendet,
              meldungen: feldMeldungen,
              hinweise,
              fehler: formFehler,
              meldung: formMeldung,
              onFeld: (feld, wert) => {
                setEntwurf({ ...entwurf, [feld]: wert });
                setFeldMeldungen({});
                setFormMeldung(null);
              },
              onSpeichern: () => {
                void formatSpeichern();
              },
              onVerwerfen: () => {
                setEntwurf(basis);
                setHinweise({});
                setFeldMeldungen({});
                setFormFehler(null);
                entwuerfe.vergessen(ENTWURFS_SCHLUESSEL);
              },
            }
          : null
      }
    />
  );
}
